import { useState } from "react";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Removed useNavigate import as navigation is handled by parent component
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserProfileData, countryToTimezone, RelationshipType } from "@/types/dashboard";
import { Phone, Mail, Users, CheckCircle2, AlertCircle, Camera, User } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import FirstContactForm from "../forms/FirstContactForm";
import SyncedContactsSelection from "./SyncedContactsSelection";
import { supabase } from "@/integrations/supabase/client";
import { toast as sonnerToast } from "sonner";
import OnboardingProgressBar from "./OnboardingProgressBar";
import WabisabiPhilosophyScreen from "./WabisabiPhilosophyScreen";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { COUNTRIES } from "@/constants/countries";

interface ProfileOnboardingProps {
  onComplete: (profileData: UserProfileData) => void;
  initialData?: Partial<UserProfileData>;
}

const ProfileOnboarding = ({ onComplete, initialData = {} }: ProfileOnboardingProps) => {
  // Navigation is handled by parent component
  const { toast } = useToast();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for philosophy screen
  const [name, setName] = useState(initialData.name || "");
  const [email, setEmail] = useState(initialData.email || "");
  const [phoneNumber, setPhoneNumber] = useState(initialData.phoneNumber || "");
  const [photoUrl, setPhotoUrl] = useState<string | null>(initialData.photoUrl || null);
  const [country, setCountry] = useState(initialData.country || "");
  const [instagramHandle, setInstagramHandle] = useState(initialData.instagramHandle || "");
  const [facebookHandle, setFacebookHandle] = useState(initialData.facebookHandle || "");
  const [contactSyncEnabled, setContactSyncEnabled] = useState(initialData.contactSyncEnabled || false);
  const [calendarSyncSettings, setCalendarSyncSettings] = useState(initialData.calendarSyncSettings || {
    enabled: false,
    syncBirthdays: true,
    syncAnniversaries: true,
    syncCustomEvents: false,
  });
  const [timezone, setTimezone] = useState(initialData.timezone || "");
  
  // New state for contact sync options
  const [contactSyncSource, setContactSyncSource] = useState<string>("google");
  const [isSyncingContacts, setIsSyncingContacts] = useState(false);
  const [syncAllContacts, setSyncAllContacts] = useState(true);
  const [availableContacts, setAvailableContacts] = useState<Array<{id: string, name: string, selected: boolean}>>([]);
  const [contactSyncComplete, setContactSyncComplete] = useState(false);
  
  // New state for first contact path
  const [firstContactCreated, setFirstContactCreated] = useState(false);
  const [showSyncedContacts, setShowSyncedContacts] = useState(false);
  const [selectedSyncedContacts, setSelectedSyncedContacts] = useState<Array<{id: string, name: string, avatar?: string}>>([]);
  
  // Mock contacts for the selective sync UI
  const mockContacts = [
    { id: '1', name: 'Alex Johnson', selected: true },
    { id: '2', name: 'Sam Williams', selected: true },
    { id: '3', name: 'Jordan Chen', selected: true },
    { id: '4', name: 'Taylor Rodriguez', selected: true },
    { id: '5', name: 'Casey Smith', selected: true },
  ];

  // Profile photo upload handler
  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handler for enabling/disabling calendar sync
  const toggleCalendarSync = (enabled: boolean) => {
    setCalendarSyncSettings({
      ...calendarSyncSettings,
      enabled,
    });
  };

  // New handler for country selection that automatically sets timezone
  const handleCountryChange = (selectedCountry: string) => {
    setCountry(selectedCountry);
    
    // Set timezone based on country
    const newTimezone = countryToTimezone[selectedCountry] || "";
    if (newTimezone) {
      setTimezone(newTimezone);
    }
  };

  // Enhanced contact sync functionality
  const handleContactSync = () => {
    setIsSyncingContacts(true);
    
    // Simulate API call to fetch contacts
    setTimeout(() => {
      setAvailableContacts(mockContacts);
      setIsSyncingContacts(false);
      
      toast({
        title: "Contacts Retrieved",
        description: `Found ${mockContacts.length} contacts from your ${contactSyncSource} account.`,
      });
    }, 1500);
  };
  
  // Toggle selection for individual contact
  const toggleContactSelection = (id: string) => {
    setAvailableContacts(contacts => 
      contacts.map(contact => 
        contact.id === id ? { ...contact, selected: !contact.selected } : contact
      )
    );
  };
  
  // Select or deselect all contacts
  const handleSelectAllContacts = (selectAll: boolean) => {
    setSyncAllContacts(selectAll);
    setAvailableContacts(contacts => 
      contacts.map(contact => ({ ...contact, selected: selectAll }))
    );
  };
  
  // Complete contact sync process
  const completeContactSync = () => {
    const selectedContacts = availableContacts.filter(c => c.selected);
    
    setContactSyncComplete(true);
    setContactSyncEnabled(true);
    
    // Store selected contacts for later use in the onboarding flow
    setSelectedSyncedContacts(selectedContacts.map(c => ({
      id: c.id,
      name: c.name,
      avatar: undefined // In a real implementation, we would have avatar URLs
    })));
    
    toast({
      title: "Contact Sync Complete",
      description: `Successfully synced ${selectedContacts.length} contacts.`,
    });
  };

  // Handle first contact submission
  const handleFirstContactSubmit = async (contactData: {
    name: string;
    relationshipType: RelationshipType;
    interests: string[];
    note?: string;
    weddingDate?: Date;
    marriageAnniversary?: Date;
    spousePreferences?: string;
    loveLanguage?: string;
    firstDate?: Date;
    anniversaryDate?: Date;
    anniversaryType?: string;
  }) => {
    try {
      // Get current user session
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      
      if (!session?.user) {
        throw new Error("User not authenticated");
      }

      // Save contact to database with relationship-specific fields
      const contactInsertData = {
        user_id: session.user.id,
        name: contactData.name,
        relationship_type: contactData.relationshipType,
        notes: contactData.note || null,
        // Add relationship-specific fields
        love_language: contactData.loveLanguage || null,
        first_date: contactData.firstDate ? contactData.firstDate.toISOString().split('T')[0] : null,
        anniversary_date: contactData.anniversaryDate ? contactData.anniversaryDate.toISOString().split('T')[0] : null,
        anniversary_type: contactData.anniversaryType || null,
        wedding_date: contactData.weddingDate ? contactData.weddingDate.toISOString().split('T')[0] : null,
        marriage_anniversary: contactData.marriageAnniversary ? contactData.marriageAnniversary.toISOString().split('T')[0] : null,
        spouse_preferences: contactData.spousePreferences || null,
      };

      const { data: contact, error: contactError } = await supabase
        .from('contacts')
        .insert(contactInsertData)
        .select()
        .single();

      if (contactError) throw contactError;

      // If contact has interests, save them as tags
      if (contactData.interests.length > 0 && contact) {
        // Create or get existing tags for these interests
        const tagPromises = contactData.interests.map(async (interest) => {
          // First try to find existing tag
          const { data: existingTag } = await supabase
            .from('tags')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('label', interest)
            .eq('type', 'interest')
            .single();

          if (existingTag) {
            return existingTag.id;
          }

          // Create new tag if it doesn't exist
          const { data: newTag, error: tagError } = await supabase
            .from('tags')
            .insert({
              user_id: session.user.id,
              label: interest,
              type: 'interest',
            })
            .select('id')
            .single();

          if (tagError) throw tagError;
          return newTag.id;
        });

        const tagIds = await Promise.all(tagPromises);

        // Create contact-tag relationships
        const contactTagsData = tagIds.map(tagId => ({
          contact_id: contact.id,
          tag_id: tagId,
        }));

        const { error: contactTagsError } = await supabase
          .from('contact_tags')
          .insert(contactTagsData);

        if (contactTagsError) throw contactTagsError;
      }


      setFirstContactCreated(true);
      
      toast({
        title: "Contact Saved",
        description: `${contactData.name} has been added to your contacts.`,
      });
      
      // Proceed to completion
      handleComplete();
    } catch (error) {
      console.error("Error saving first contact:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your contact.",
        variant: "destructive",
      });
    }
  };

  // Handle synced contact selection
  const handleSyncedContactSelect = async (contactData: {
    id: string;
    name: string;
    relationshipType?: RelationshipType;
    interests: string[];
    note?: string;
  }) => {
    try {
      // Get current user session
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      
      if (!session?.user) {
        throw new Error("User not authenticated");
      }

      // Save contact to database (this is a new contact based on synced data)
      const { data: contact, error: contactError } = await supabase
        .from('contacts')
        .insert({
          user_id: session.user.id,
          name: contactData.name,
          relationship_type: contactData.relationshipType || 'Other',
          notes: contactData.note || null,
        })
        .select()
        .single();

      if (contactError) throw contactError;

      // If contact has interests, save them as tags
      if (contactData.interests.length > 0 && contact) {
        // Create or get existing tags for these interests
        const tagPromises = contactData.interests.map(async (interest) => {
          // First try to find existing tag
          const { data: existingTag } = await supabase
            .from('tags')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('label', interest)
            .eq('type', 'interest')
            .single();

          if (existingTag) {
            return existingTag.id;
          }

          // Create new tag if it doesn't exist
          const { data: newTag, error: tagError } = await supabase
            .from('tags')
            .insert({
              user_id: session.user.id,
              label: interest,
              type: 'interest',
            })
            .select('id')
            .single();

          if (tagError) throw tagError;
          return newTag.id;
        });

        const tagIds = await Promise.all(tagPromises);

        // Create contact-tag relationships
        const contactTagsData = tagIds.map(tagId => ({
          contact_id: contact.id,
          tag_id: tagId,
        }));

        const { error: contactTagsError } = await supabase
          .from('contact_tags')
          .insert(contactTagsData);

        if (contactTagsError) throw contactTagsError;
      }

      setFirstContactCreated(true);
      
      toast({
        title: "Contact Saved",
        description: `${contactData.name} has been added to your contacts.`,
      });
      
      // Proceed to completion
      handleComplete();
    } catch (error) {
      console.error("Error saving contact:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your contact.",
        variant: "destructive",
      });
    }
  };

  // Handle onboarding completion
  const handleComplete = () => {
    if (!name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your name to continue.",
        variant: "destructive",
      });
      return;
    }

    const profileData: UserProfileData = {
      name,
      email,
      phoneNumber,
      photoUrl: photoUrl || undefined,
      country,
      timezone,
      instagramHandle,
      facebookHandle,
      contactSyncEnabled,
      calendarSyncSettings,
      preferredContactMethod: "email", // Default to email
      onboardingCompleted: true,
      syncedContactSources: contactSyncEnabled ? [contactSyncSource] : [],
    };

    onComplete(profileData);
    
    // Show completion toast - navigation is handled by the Profile page
    sonnerToast("🎉 You're in. Let's start remembering what matters.", {
      description: "Welcome to your personal relationship manager!",
      position: "top-center",
      duration: 4000,
    });
  };

  // Step navigation - updated for new step 0
  const nextStep = () => {
    if (currentStep === 1 && !name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your name to continue.",
        variant: "destructive",
      });
      return;
    }
    
    if (currentStep === 3) {
      // When moving from step 3 to step 4, check if contacts were synced
      // and prepare synced contacts UI if applicable
      if (contactSyncComplete && selectedSyncedContacts.length > 0) {
        // Limit to 5 contacts maximum for better UI
        setSelectedSyncedContacts(selectedSyncedContacts.slice(0, 5));
        setShowSyncedContacts(true);
      } else {
        setShowSyncedContacts(false);
      }
    }
    
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep === 4 && showSyncedContacts) {
      // If going back from synced contacts view, just hide it but stay on step 4
      setShowSyncedContacts(false);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // Removed unused variable isLastStepBeforeContact
  const maxSteps = 5; // Updated to include philosophy screen

  // Determine if email and name should be read-only (if they were provided in initialData)
  const isEmailReadOnly = !!initialData.email;
  const isNameReadOnly = !!initialData.name;

  return (
    <TooltipProvider>
      {currentStep > 0 && (
        <OnboardingProgressBar currentStep={currentStep} totalSteps={maxSteps} className="max-w-md mx-auto" />
      )}
      
      {/* Step 0: Philosophy Screen */}
      {currentStep === 0 && (
        <WabisabiPhilosophyScreen onContinue={() => setCurrentStep(1)} />
      )}
      
      {/* Steps 1-4: Existing onboarding flow */}
      {currentStep > 0 && (
        <Card className="w-full max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-center">
              {currentStep === 1 && "Welcome! Let's set up your profile"}
              {currentStep === 2 && "Help us personalise your experience"}
              {currentStep === 3 && "Connect what matters"}
              {currentStep === 4 && "Who's someone you don't want to forget the little things about?"}
            </CardTitle>
            {currentStep === 1 && (
              <CardDescription className="text-center">
                <Tooltip>
                  <TooltipTrigger>
                    We'll use this info to personalise your Wabisabi experience — and help you remember what matters.
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Your profile doesn't need to be perfect – just authentic to you</p>
                  </TooltipContent>
                </Tooltip>
              </CardDescription>
            )}
            {currentStep === 3 && (
              <CardDescription className="text-center">
                <Tooltip>
                  <TooltipTrigger>
                    Import your contacts so Wabisabi can help you remember the people you care about.
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This isn't about perfect tracking – it's about gentle reminders for what matters</p>
                  </TooltipContent>
                </Tooltip>
              </CardDescription>
            )}
            {currentStep < maxSteps && <CardDescription className="text-center mt-1">Almost there</CardDescription>}
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Step 1: Basic Profile */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="flex justify-center mb-6">
                  <UserAvatar 
                    src={photoUrl} 
                    name={name || "?"}
                    size="large"
                    editable={true}
                    onImageUpload={handleImageUpload}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name">What should we call you?</Label>
                  <Input 
                    id="name"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    readOnly={isNameReadOnly}
                    className={isNameReadOnly ? "bg-gray-50" : ""}
                  />
                  {isNameReadOnly && (
                    <p className="text-xs text-muted-foreground">
                      Name provided from your sign-up
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={isEmailReadOnly}
                    className={isEmailReadOnly ? "bg-gray-50" : ""}
                  />
                  {isEmailReadOnly && (
                    <p className="text-xs text-muted-foreground">
                      Email provided from your sign-up
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional – Add your phone number for SMS reminders
                  </p>
                </div>
              </div>
            )}
            
            {/* Step 2: Location & Preferences */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Your Country</Label>
                  <Select
                    value={country}
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Tell us where you are so we remember what matters when it matters.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={timezone}
                    onValueChange={setTimezone}
                  >
                    <SelectTrigger id="timezone">
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GMT-8">Pacific Time (GMT-8)</SelectItem>
                      <SelectItem value="GMT-7">Mountain Time (GMT-7)</SelectItem>
                      <SelectItem value="GMT-6">Central Time (GMT-6)</SelectItem>
                      <SelectItem value="GMT-5">Eastern Time (GMT-5)</SelectItem>
                      <SelectItem value="GMT+0">Greenwich Mean Time (GMT)</SelectItem>
                      <SelectItem value="GMT+1">Central European Time (GMT+1)</SelectItem>
                      <SelectItem value="GMT+5:30">India Standard Time (GMT+5:30)</SelectItem>
                      <SelectItem value="GMT+8">China Standard Time (GMT+8)</SelectItem>
                      <SelectItem value="GMT+9">Japan Standard Time (GMT+9)</SelectItem>
                      <SelectItem value="GMT+10">Australian Eastern Time (GMT+10)</SelectItem>
                      <SelectItem value="GMT+12">New Zealand Time (GMT+12)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Automatically set based on your country selection
                  </p>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3">
                  <h3 className="font-medium">Social Profiles</h3>
                  <p className="text-xs text-muted-foreground">
                    Optional — helps us enrich context if you choose to connect later.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-pink-500" />
                      <Input 
                        placeholder="Instagram username (without @)"
                        value={instagramHandle}
                        onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <Input 
                        placeholder="Facebook username"
                        value={facebookHandle}
                        onChange={(e) => setFacebookHandle(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Synchronization */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {/* Contact Sync Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Contact Synchronization</Label>
                      <p className="text-sm text-muted-foreground">
                        Import contacts from your devices or accounts
                      </p>
                    </div>
                  </div>

                  {!contactSyncComplete ? (
                    <>
                      {availableContacts.length === 0 ? (
                        <div className="space-y-4">
                          {/* Contact Source Selection */}
                          <RadioGroup 
                            value={contactSyncSource} 
                            onValueChange={setContactSyncSource}
                            className="flex flex-col space-y-2"
                          >
                            <div className="flex items-center space-x-2 p-2 border rounded-md">
                              <RadioGroupItem value="google" id="google" />
                              <Label htmlFor="google" className="flex-1 cursor-pointer">Google Contacts</Label>
                            </div>
                            <div className="flex items-center space-x-2 p-2 border rounded-md">
                              <RadioGroupItem value="apple" id="apple" />
                              <Label htmlFor="apple" className="flex-1 cursor-pointer">Apple Contacts</Label>
                            </div>
                            <div className="flex items-center space-x-2 p-2 border rounded-md">
                              <RadioGroupItem value="phone" id="phone" />
                              <Label htmlFor="phone" className="flex-1 cursor-pointer">Phone Contacts</Label>
                            </div>
                          </RadioGroup>

                          <Button 
                            onClick={handleContactSync}
                            disabled={isSyncingContacts}
                            className="w-full"
                          >
                            {isSyncingContacts ? (
                              <>Loading Contacts...</>
                            ) : (
                              <>Connect {contactSyncSource === "google" ? "Google" : contactSyncSource === "apple" ? "Apple" : "Phone"} Contacts</>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Contact Selection UI */}
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <h4 className="text-sm font-medium">Select Contacts to Import</h4>
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="select-all" 
                                    checked={syncAllContacts}
                                    onCheckedChange={(checked) => handleSelectAllContacts(!!checked)}
                                  />
                                  <Label htmlFor="select-all" className="text-sm">Select All</Label>
                                </div>
                              </div>
                            </div>

                            <div className="border rounded-md max-h-48 overflow-y-auto">
                              {availableContacts.map((contact) => (
                                <div 
                                  key={contact.id} 
                                  className="flex items-center p-2 hover:bg-muted"
                                >
                                  <Checkbox 
                                    id={`contact-${contact.id}`}
                                    checked={contact.selected}
                                    onCheckedChange={() => toggleContactSelection(contact.id)}
                                    className="mr-3"
                                  />
                                  <Label 
                                    htmlFor={`contact-${contact.id}`}
                                    className="flex-1 cursor-pointer text-sm"
                                  >
                                    {contact.name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={() => setAvailableContacts([])}
                              className="flex-1"
                            >
                              Back
                            </Button>
                            <Button
                              onClick={completeContactSync}
                              className="flex-1"
                            >
                              Import Selected
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Contacts synced successfully</p>
                        <p className="text-xs text-muted-foreground">
                          {availableContacts.filter(c => c.selected).length} contacts imported from {contactSyncSource}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setContactSyncComplete(false);
                          setAvailableContacts([]);
                        }}
                      >
                        Change
                      </Button>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="calendar-sync">Calendar Synchronization</Label>
                      <p className="text-sm text-muted-foreground">
                        Add important dates from your calendar to make sure you never miss a moment
                      </p>
                    </div>
                    <Switch 
                      id="calendar-sync" 
                      checked={calendarSyncSettings.enabled}
                      onCheckedChange={toggleCalendarSync}
                    />
                  </div>
                  
                  {calendarSyncSettings.enabled && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sync-birthdays">Birthdays</Label>
                        <Switch 
                          id="sync-birthdays" 
                          checked={calendarSyncSettings.syncBirthdays}
                          onCheckedChange={(checked) => 
                            setCalendarSyncSettings({
                              ...calendarSyncSettings,
                              syncBirthdays: checked,
                            })
                          }
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sync-anniversaries">Anniversaries</Label>
                        <Switch 
                          id="sync-anniversaries" 
                          checked={calendarSyncSettings.syncAnniversaries}
                          onCheckedChange={(checked) => 
                            setCalendarSyncSettings({
                              ...calendarSyncSettings,
                              syncAnniversaries: checked,
                            })
                          }
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sync-custom-events">Custom Events</Label>
                        <Switch 
                          id="sync-custom-events" 
                          checked={calendarSyncSettings.syncCustomEvents}
                          onCheckedChange={(checked) => 
                            setCalendarSyncSettings({
                              ...calendarSyncSettings,
                              syncCustomEvents: checked,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-muted-foreground mt-4 italic">
                  We never message anyone or share your data — this is just for you.
                </p>
              </div>
            )}

            {/* Step 4: First Contact - With Synced Contacts or Manual Entry */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Quality over quantity – focus on relationships that truly matter to you
                </p>
                {contactSyncComplete && selectedSyncedContacts.length > 0 && showSyncedContacts ? (
                  <SyncedContactsSelection 
                    contacts={selectedSyncedContacts}
                    onContactSelect={handleSyncedContactSelect}
                  />
                ) : (
                  <FirstContactForm onComplete={handleFirstContactSubmit} />
                )}
              </div>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-between">
            {currentStep > 1 ? (
              <Button variant="outline" onClick={prevStep}>Back</Button>
            ) : (
              <div></div> // Empty div to maintain the flex layout
            )}
            
            {currentStep < maxSteps && currentStep !== 4 ? (
              <Button onClick={nextStep}>Keep going</Button>
            ) : (
              <div></div> // Empty div since we handle the submission in FirstContactForm or SyncedContactsSelection
            )}
          </CardFooter>
        </Card>
      )}
    </TooltipProvider>
  );
};

export default ProfileOnboarding;
